import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useLocation, useNavigate } from 'react-router';

import { AgreementDTO } from '@/types/api-types';
import { NormalizedAgreement } from '@/types/file-explorer-types';

import { cn } from '@/lib/utils';

import { useCalculateRowsPerPage } from '@/hooks/use-calculate-rows-per-page';
import { useIsMobile } from '@/hooks/use-mobile';

import { Checkbox } from '@/components/ui/checkbox';
import { CheckboxSome } from '@/components/ui/checkbox-some';
import { DetailsPanel } from '@/components/DetailsPanel/DetailsPanel';
import AgreementSelectionBar from '@/components/FileExplorer/AgreementSelectionBar';
import TableActionsComponent from '@/components/FileExplorer/TableActionsComponent';
import {
  ColumnHeaderAgreement,
  getActionsColumn,
} from '@/components/FileExplorer/TableColumns';
import TableRowSkeleton from '@/components/TableRowSkeleton';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useFiltersStore } from '@/stores/filtersStore';
import { useGlobalStore } from '@/stores/globalStore';
import { useSearchStore } from '@/stores/searchStore';

import { ROW_HEIGHT } from '@/constants/global-constants';
import { executorRoutes } from '@/constants/route-settings';
import {
  ExplorerItemType,
  getColumnsConfig,
} from '@/constants/table-columns-config';

export function FileSearchResultPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateAgreement } = useApiManagerStore();
  const rowsPerPage = useCalculateRowsPerPage(ROW_HEIGHT);

  const {
    sorting,
    setSorting,
    visibleColumns,
    setVisibleColumns,
    agreementsState,
    setAgreementsState,
    getAgreementsStateSearch,
  } = useFileExplorerStore();

  const { getFilterByIds, pagination } = useFiltersStore();
  const {
    searchPageResults,
    setSearchPageResults,
    searchPageTerm,
    setSearchPageTerm,
  } = useSearchStore();
  const { setNavigationSource } = useGlobalStore();
  const isMobile = useIsMobile();

  // Local state
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<NormalizedAgreement | null>(
    null,
  );
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [lastSearchAgreementsIds, setLastSearchAgreementsIds] = useState<
    string[]
  >([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  // const [originalSearchTerm, setOriginalSearchTerm] = useState<string>('');
  const [totalFoundCount, setTotalFoundCount] = useState<number>(0);

  const handleNavigate = (uuid: string, entityType: ExplorerItemType) => {
    const typeParam = (() => {
      switch (entityType) {
        case ExplorerItemType.DIRECTORY:
          return 'dir';
        case ExplorerItemType.INSURANCE_OBJECT:
          return 'obj';
        case ExplorerItemType.INSURANCE_AREA:
          return 'area';
        case ExplorerItemType.AGREEMENT:
          return 'agree';
        case ExplorerItemType.FILE:
          return 'file';
        default:
          return 'agree';
      }
    })();

    // Set navigation source for back button
    setNavigationSource('searchResults');
    navigate(`${executorRoutes.myDocuments}?t=${typeParam}&id=${uuid}`);
  };
  const handleEditAgreement = async (values: AgreementDTO) => {
    const updatedAgreement = await updateAgreement(values.uuid, values);
    setSelectedRow(
      (prev) => ({ ...prev, ...updatedAgreement }) as NormalizedAgreement,
    );
  };

  // Column configurations
  const getRegularColumns = (): ColumnDef<NormalizedAgreement>[] => {
    return getColumnsConfig(ExplorerItemType.AGREEMENT)
      .filter((column) => visibleColumns.includes(column.accessorKey as string))
      .map((column) => ({
        ...column,
        header: ({
          column: tableColumn,
        }: {
          column: Column<NormalizedAgreement>;
        }) => (
          <ColumnHeaderAgreement
            column={tableColumn}
            currentPage={'search'}
            openFilterColumn={openFilterColumn}
            setOpenFilterColumn={setOpenFilterColumn}
          />
        ),
      })) as ColumnDef<NormalizedAgreement>[];
  };

  const selectionColumn: ColumnDef<NormalizedAgreement> | null = useMemo(() => {
    return {
      id: 'select',
      header: ({ table }) => (
        <div className="flex items-center">
          {table.getIsSomeRowsSelected() ? (
            <CheckboxSome
              checked={table.getIsSomeRowsSelected()}
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          ) : (
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          )}
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        </div>
      ),
      size: 28,
      enableSorting: false,
      enableResizing: false,
    } as ColumnDef<NormalizedAgreement>;
  }, []);

  const columns = [
    selectionColumn!,
    ...getRegularColumns(),
    getActionsColumn({
      setIsDetailsPanelOpen,
      setSelectedRow,
    }),
  ].filter(Boolean) as ColumnDef<NormalizedAgreement>[];

  useEffect(() => {
    const agreementIds = location.state?.agreementIds;
    const term = location.state?.searchTerm;

    if (agreementIds) {
      // Fresh navigation from search page - use location.state
      setLastSearchAgreementsIds(agreementIds);
      setSearchPageResults(agreementIds);
      setTotalFoundCount(agreementIds.length);
    } else if (searchPageResults.length > 0) {
      // Returning via back button - restore from store
      setLastSearchAgreementsIds(searchPageResults);
      setTotalFoundCount(searchPageResults.length);
    }

    if (term) {
      setSearchTerm(term);
      setSearchPageTerm(term);
    } else if (searchPageTerm) {
      // Restore search term from store when returning via back button
      setSearchTerm(searchPageTerm);
    }
  }, [location]);

  const agreementItemsFetch = useCallback(async () => {
    await getAgreementsStateSearch(lastSearchAgreementsIds);
  }, [lastSearchAgreementsIds]);

  const agreementItems = useMemo(() => {
    return Object.values(agreementsState.entities);
  }, [agreementsState.entities]);

  useEffect(() => {
    if (lastSearchAgreementsIds.length === 0) return;
    let isActive = true;
    setIsLoading(true);
    agreementItemsFetch().then(() => {
      if (!isActive) return;
      setIsLoading(false);
    });
    return () => {
      isActive = false;
    };
  }, [
    JSON.stringify(getFilterByIds('search')),
    lastSearchAgreementsIds,
    JSON.stringify(sorting),
    JSON.stringify(pagination),
  ]);

  useEffect(() => {
    // Only clear agreements state if we have fresh search data from location.state
    // When returning via back button, location.state is null and we restore from store
    if (location.state?.agreementIds) {
      setAgreementsState({
        entities: {},
        rootIds: [],
        entityTypeLevel: ExplorerItemType.AGREEMENT,
        totalCount: 0,
      });
    }

    return () => {
      // Don't clear searchPageResults on unmount - they're needed for back navigation
    };
  }, []);

  const table = useReactTable({
    data: agreementItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    getRowId: (row) => row.uuid,
    filterFns: {
      exact: (row, columnId, value): boolean => {
        let cellValue = '';
        if (columnId === 'step') {
          cellValue =
            (useGlobalStore.getState().steps ?? [])
              .find((step) => step.uuid === row.original.step)
              ?.status_name.toLowerCase() || '';
        } else {
          cellValue = String(row.getValue(columnId) || '').toLowerCase();
        }

        const filterValue = String(value).toLowerCase();
        return filterValue === '' || cellValue === filterValue;
      },
    },
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    autoResetAll: false,
  });

  return (
    <div className="flex h-full w-full">
      <div className="flex-grow px-3 py-5 sm:px-[50px] sm:py-[50px]">
        <div className={cn('relative flex h-full w-full')}>
          <div
            className={cn(
              `relative box-border flex h-full w-full flex-1 flex-col gap-1 overflow-x-hidden rounded-md bg-white transition-all duration-300 ease-in-out`,
              isDetailsPanelOpen ? 'sm:mr-[574px] sm:w-[calc(100%-574px)]' : '',
              'shadow-sm',
            )}
          >
            {table.getSelectedRowModel().rows.length === 0 ? (
              <TableActionsComponent
                isSmall={false}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                isLoading={isLoading}
                hasMoreItemsAvailable={
                  agreementItems.length ===
                  (pagination?.pageSize || rowsPerPage)
                }
                entityType={ExplorerItemType.AGREEMENT}
                currentPage={'search'}
              />
            ) : null}
            <div className="flex items-baseline pl-[20px] pt-6 sm:pl-9 sm:pt-[24px]">
              <h1 className="text-2xl font-medium">
                Результати пошуку: {searchTerm}
              </h1>
              <div className="ml-4 text-sm text-gray-600">
                Знайдено: {totalFoundCount}
              </div>
            </div>

            {/* Table container with fixed width and horizontal scroll */}
            <div
              className={cn(
                'w-full flex-grow',
                isMobile ? 'overflow-auto' : 'relative h-full overflow-y-auto',
              )}
            >
              {/* Different containers based on mobile vs. desktop */}
              <div
                className={cn(
                  isMobile
                    ? 'w-full overflow-x-auto'
                    : 'absolute inset-0 overflow-auto',
                )}
              >
                <AgreementSelectionBar table={table} />
                <table
                  className={cn(
                    'w-full border-separate border-spacing-0',
                    isMobile ? 'min-w-[340px]' : 'min-w-[640px] max-w-none',
                  )}
                >
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={`w-auto border-b px-[20px] py-5 text-left text-base font-medium sm:px-4 sm:first:pl-[36px] sm:last:pr-[60px]`}
                            style={{
                              width:
                                header.column.id === 'name' &&
                                columns.length < 4
                                  ? '100%'
                                  : 'auto',
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <TableRowSkeleton columnCount={columns.length} />
                    ) : (
                      table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          className={cn(
                            'cursor-pointer border-b border-gray-200',
                            row.original.has_file_errors && 'bg-[#FFF5F5]',
                          )}
                          onClick={() => {
                            handleNavigate(
                              row.original.uuid,
                              ExplorerItemType.FILE,
                            );
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className={`sm:px-4h-[60px] border-b border-separator px-[20px] py-3 sm:h-[70px] sm:first:pl-[36px] sm:last:pr-[60px]`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {selectedRow !== null && (
            <DetailsPanel
              isOpen={isDetailsPanelOpen}
              onClose={() => {
                setIsDetailsPanelOpen(false);
                setSelectedRow(null);
              }}
              data={selectedRow}
              onEdit={handleEditAgreement}
              directoryAbbreviation={
                agreementItems.find((item) => item.uuid === selectedRow.uuid)
                  ?.directory_name || ''
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
